<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import PostSaleAgenda from './PostSaleAgenda.vue';
import PostSaleCasesTable from './PostSaleCasesTable.vue';
import PostSaleManagement from './PostSaleManagement.vue';

const props = defineProps({
  management: { type: Object, required: true },
  postSale: { type: Object, required: true },
  sectionTabs: { type: Array, required: true },
});

const emit = defineEmits([
  'tableAction',
  'changeSection',
  'change-management-tab',
  'open-agenda-action',
  'open-case',
  'refresh-queue',
  'set-period',
  'toggle-queue',
  'view-filtered',
]);
const { t } = useI18n();
const collapsed = ref(false);
const salesOpen = ref(true);
const salesLists = computed(() => [
  {
    id: 'andamento',
    label: t('POST_SALE.LISTS.IN_PROGRESS'),
    count: props.postSale.counts.value.andamento,
  },
  {
    id: 'revisoes',
    label: t('POST_SALE.LISTS.REVIEW'),
    count: props.postSale.counts.value.revisoes,
  },
  {
    id: 'finalizadas',
    label: t('POST_SALE.LISTS.COMPLETED'),
    count: props.postSale.counts.value.finalizadas,
  },
  {
    id: 'cancelados',
    label: t('POST_SALE.LISTS.CANCELED'),
    count: props.postSale.counts.value.cancelados,
  },
]);

const selectSection = section => {
  if (section === 'vendas') salesOpen.value = true;
  emit('changeSection', section);
};
const selectList = list => {
  if (props.postSale.activeSection.value !== 'vendas') {
    emit('changeSection', 'vendas');
  }
  props.postSale.selectList(list);
};
</script>

<template>
  <div
    data-testid="post-sale-index-sections"
    class="flex min-h-[calc(100vh-4rem)] min-w-0 flex-col bg-lais-bg-soft md:flex-row"
  >
    <aside
      data-testid="post-sale-section-navigation"
      class="hidden h-[calc(100vh-4rem)] shrink-0 flex-col overflow-y-auto border-r border-lais-border-soft bg-white transition-[width] duration-700 md:sticky md:top-0 md:flex"
      :class="collapsed ? 'w-[106px]' : 'w-[295px] lg:w-[215px]'"
      :aria-label="t('POST_SALE.ARIA.SECTIONS')"
    >
      <div class="flex h-16 justify-end border-b border-lais-border-soft p-3">
        <button
          type="button"
          class="rounded-lais p-2 text-[#01988C] transition hover:bg-[#E9F7F6]"
          :aria-expanded="!collapsed"
          :aria-label="
            collapsed
              ? t('POST_SALE.NAVIGATION.EXPAND')
              : t('POST_SALE.NAVIGATION.COLLAPSE')
          "
          @click="collapsed = !collapsed"
        >
          <span
            class="i-lucide-chevrons-left size-5"
            :class="collapsed ? 'rotate-180' : ''"
            aria-hidden="true"
          />
        </button>
      </div>

      <nav class="space-y-2 p-4">
        <button
          v-for="tab in sectionTabs"
          :key="tab.id"
          type="button"
          class="flex h-14 w-full items-center gap-4 rounded-lais px-5 text-left text-base font-bold transition hover:bg-lais-bg-soft"
          :class="
            postSale.activeSection.value === tab.id
              ? 'bg-[#E9F7F6] text-[#01988C]'
              : 'text-lais-text-body'
          "
          :aria-current="
            postSale.activeSection.value === tab.id ? 'page' : undefined
          "
          :aria-expanded="tab.id === 'vendas' ? salesOpen : undefined"
          @click="selectSection(tab.id)"
        >
          <span
            class="size-5 shrink-0 text-[#01988C]"
            :class="tab.icon"
            aria-hidden="true"
          />
          <span v-if="!collapsed" class="min-w-0 flex-1 truncate">
            {{ tab.label }}
          </span>
          <span
            v-if="tab.id === 'vendas' && !collapsed"
            class="size-4"
            :class="salesOpen ? 'i-lucide-chevron-up' : 'i-lucide-chevron-down'"
            aria-hidden="true"
          />
        </button>

        <ul
          v-if="salesOpen && !collapsed"
          class="list-none space-y-1 pl-5"
          data-testid="post-sale-sales-lists"
        >
          <li v-for="list in salesLists" :key="list.id">
            <button
              type="button"
              class="flex h-14 w-full items-center rounded-lais px-5 text-left text-base font-bold transition hover:bg-lais-bg-soft"
              :class="
                postSale.activeSection.value === 'vendas' &&
                postSale.activeList.value === list.id
                  ? 'bg-[#E9F7F6] text-[#01988C]'
                  : 'text-lais-text-body'
              "
              :aria-current="
                postSale.activeSection.value === 'vendas' &&
                postSale.activeList.value === list.id
                  ? 'page'
                  : undefined
              "
              @click="selectList(list.id)"
            >
              <span class="min-w-0 flex-1 truncate">{{ list.label }}</span>
              <span v-if="list.count" class="ml-2 text-sm">
                {{ list.count }}
              </span>
            </button>
          </li>
        </ul>
      </nav>
    </aside>

    <section class="min-w-0 flex-1">
      <template v-if="postSale.activeSection.value === 'gestao'">
        <slot name="gestao">
          <PostSaleManagement
            :active-tab="postSale.managementTab.value"
            :dashboard="postSale.dashboard.value"
            :deeds="management.deeds.value"
            :deeds-loading="management.loadingDeeds.value"
            :loading="postSale.loadingDashboard.value"
            :period="management.period.value"
            :queue-loading="management.loadingDashboard.value"
            :queue-saving-user-id="management.savingQueueUserId.value"
            :queue-updated-at="management.queueUpdatedAt.value"
            @change-tab="emit('change-management-tab', $event)"
            @open-case="emit('open-case', $event)"
            @refresh-queue="emit('refresh-queue')"
            @set-period="emit('set-period', $event)"
            @toggle-queue="emit('toggle-queue', $event)"
            @view-filtered="emit('view-filtered', $event)"
          />
        </slot>
      </template>
      <template v-else-if="postSale.activeSection.value === 'prioridades'">
        <slot name="prioridades">
          <PostSaleAgenda
            :navigation-collapsed="collapsed"
            :responsibles="postSale.agents.value"
            @action="emit('open-agenda-action', $event)"
            @open="emit('open-case', $event)"
          />
        </slot>
      </template>
      <template v-else>
        <slot name="vendas">
          <PostSaleCasesTable
            :active-list="postSale.activeList.value"
            :address-options="postSale.addresses.value"
            :addresses-loading="postSale.loadingAddresses.value"
            :agents="postSale.agents.value"
            :cases="postSale.cases.value"
            :client-options="postSale.clients.value"
            :clients-loading="postSale.loadingClients.value"
            :counts="postSale.counts.value"
            :error="postSale.error.value"
            :filters="postSale.filters.value"
            :loading="postSale.loading.value"
            :page="postSale.page.value"
            :sort="postSale.sort.value"
            :total-pages="postSale.totalPages.value"
            :total-rows="postSale.totalRows.value"
            @apply-filters="postSale.applyFilters"
            @apply-sort="postSale.applySort"
            @action="emit('tableAction', $event)"
            @change-page="postSale.changePage"
            @clear-filters="postSale.clearFilters"
            @open="emit('open-case', $event)"
            @search-addresses="postSale.loadAddresses"
            @select-list="postSale.selectList"
          />
        </slot>
      </template>
    </section>
  </div>
</template>
